'use client';

import { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/navigation';
import { Box } from '@mui/material';
import { isFalsy } from 'utility-types';
import { createAppStore, getAppStoreByStoreName } from '@/api/appstore.api';
import type { BaseModalProps } from '@/interfaces/component/modal.interface';
import type { StoreData } from '@/interfaces/service/appstore/appstore.search.interface';
import { useAppStoreStore } from '@/stores/appstore.store';
import { useIndicatorStore } from '@/stores/indicator.store';
import { useSearchStore } from '@/stores/search.store';
import { useToastStore } from '@/stores/toast.store';
import { REVIEW_ROUTES } from '@/constants/path.constants';
import { getToken } from '@/utils/auth.util';
import { createRunGate, runStoreRegistration, shouldOpenDashboard } from '@/utils/storeRegistration';
import BaseModal from '@/components/modal/BaseModal';
import SearchBar from '@/components/modal/search/components/SearchBar';
import SearchResultList from '@/components/modal/search/components/SearchResultList';
import { usePatchStoreSwitch } from '@/libs/hooks/appstore';

const checkSelectedStoreNotEmpty = (stores: StoreData) => {
  if (isFalsy(stores)) {
    return false;
  }

  return Object.entries(stores).filter(([key, value]) => value !== undefined)?.length > 0;
};

const SearchModal = (props: { showModal: boolean; handleShowModal: () => void; searchWord?: string; type: 'SEARCH' | 'CHANGE' }) => {
  const { showModal, handleShowModal, searchWord, type } = props;

  const router = useRouter();

  // mutate 는 내부 거절을 삼키고 void 를 돌려준다 — await 해도 기다리지 않고 실패도 못 받는다.
  // 등록 후 매장 전환 실패를 여기서 감지해야 하므로 mutateAsync 를 쓴다.
  const { mutateAsync: mutatePatchStoreSwitch } = usePatchStoreSwitch();

  const { setAppStores, pushOnCrawlingAppStoreId } = useAppStoreStore((state) => state);
  const { selectedStores, searchResults, clearSearchStore } = useSearchStore((state) => state);

  const setShowIndicator = useIndicatorStore((state) => state.setShowIndicator);
  const { setShowToast, setToastInfos } = useToastStore((state) => state);

  const [_showModal, _setShowModal] = useState(showModal);

  const [submitable, setSubmitable] = useState(checkSelectedStoreNotEmpty(selectedStores));

  // 실행 식별자와 전역 인디케이터 소유권. 닫았다 다시 열면 이전 요청은 무효가 되고,
  // 인디케이터는 켠 실행만 내릴 수 있다.
  const gateRef = useRef(createRunGate());
  const mountedRef = useRef(true);
  // 이 모달은 호출부에 따라 언마운트되지 않고 표시 여부만 바뀐다(예: organization/teams).
  // 그래서 "닫힘" 을 마운트 여부로 판단하면 사용자가 취소한 등록이 계속 진행된다.
  const visibleRef = useRef(showModal);

  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
      // 인디케이터는 이 모달 밖에 있는 전역 백드롭이다. 우리가 켠 상태로 사라지면
      // 아무도 내릴 수 없다(라우팅이 같은 경로면 Indicator 의 pathname 감지도 안 걸린다).
      gateRef.current.cancel();

      if (gateRef.current.releaseAny()) {
        setShowIndicator(false);
      }

      clearSearchStore();
    };
  }, []);

  useEffect(() => {
    visibleRef.current = showModal;

    if (showModal) {
      return;
    }

    // 닫혔다. 진행 중이던 실행을 무효로 만들고, 그 실행이 켜 둔 인디케이터를 내린다.
    // 언마운트가 아니라 cleanup 이 안 도는 호출부가 있어 여기서도 정리해야 한다.
    gateRef.current.cancel();

    if (gateRef.current.releaseAny()) {
      setShowIndicator(false);
    }
  }, [showModal]);

  useEffect(() => {
    setSubmitable(checkSelectedStoreNotEmpty(selectedStores));
  }, [selectedStores]);

  const notify = (text: string) => {
    setToastInfos({ text });
    setShowToast(true);
  };

  const createAndAnalyzeReview = (_selectedStores: StoreData) => {
    // 이 클릭의 실행 id. 이후 응답은 전부 이 id 로 유효성을 판단한다.
    // 이전 실행이 인디케이터를 켜 둔 채였다면 그 소유권을 회수했으므로 여기서 내린다
    // (그 실행은 방금 무효가 되어 스스로 내리지 못한다).
    const { id: runId, hadOwner } = gateRef.current.begin();

    if (hadOwner) {
      setShowIndicator(false);
    }

    return runStoreRegistration(_selectedStores?.NAVER, {
      findStore: getAppStoreByStoreName,
      createStore: createAppStore,
      switchStore: params => mutatePatchStoreSwitch(params),
      setIndicator: show => {
        if (show) {
          gateRef.current.claim(runId);
          setShowIndicator(true);

          return;
        }

        // 이미 다른 실행이 켠 뒤라면 남의 것이다. 소유자일 때만 내린다.
        if (gateRef.current.release(runId)) {
          setShowIndicator(false);
        }
      },
      notify,
      applyStore: (store, isNew) => {
        setAppStores([store], store.storeId);

        if (isNew) {
          pushOnCrawlingAppStoreId(store.storeId);
        }
      },
      navigate: (store, isNew) => {
        router.push(shouldOpenDashboard(store, isNew) ? REVIEW_ROUTES.getStoreDashboard(store.storeId) : REVIEW_ROUTES.getReviews(store.storeId));
      },
      hasToken: () => Boolean(getToken()),
      isActive: () => mountedRef.current && visibleRef.current && gateRef.current.isCurrent(runId)
    });
  };

  const modalProps: BaseModalProps = {
    showModal: _showModal,
    modalTitle: type === 'SEARCH' ? '병원 검색' : '병원 설정',
    onCloseModal: () => {
      _setShowModal(false);
    },
    handleShowModal,
    buttons: {
      primary: {
        title: type === 'SEARCH' ? '선택 완료하기' : '병원 설정하기',
        onClick: () => createAndAnalyzeReview(selectedStores),
        disabled: !submitable
      }
    }
  };

  return (
    <BaseModal {...modalProps} showModal={showModal} handleShowModal={handleShowModal} isFullHeight={true}>
      <SearchBar searchWord={searchWord} />
      <Box sx={{ overflowY: 'auto', flex: 1, width: 1 }}>{searchResults.NAVER?.list ? <SearchResultList /> : ''}</Box>
    </BaseModal>
  );
};

export default SearchModal;
